import React, { useState } from 'react';
import styles from './Accordion.module.css';

export interface AccordionItemProps {
  id?: string;
  title: string;
  children: React.ReactNode;
  /** Controlled open state. When provided, `onToggle` should be too. */
  isOpen?: boolean;
  onToggle?: () => void;
  /** Initial open state when uncontrolled. Defaults to true. */
  defaultOpen?: boolean;
  /** When false, the body is always visible and the chevron is hidden. */
  collapsible?: boolean;
  toggleTextOpen?: string;
  toggleTextClosed?: string;
  /** Optional lighter-weight content rendered between the title and the chevron (e.g. a count). */
  headerTrailing?: React.ReactNode;
  /** Optional interactive content rendered next to the chevron (e.g. action buttons). Clicks do not toggle the accordion. */
  headerAction?: React.ReactNode;
  /** Optional content rendered before the title (e.g. an Icon). */
  leading?: React.ReactNode;
  noPadding?: boolean;
  className?: string;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  title,
  children,
  isOpen: controlledIsOpen,
  onToggle: controlledOnToggle,
  defaultOpen = true,
  collapsible = true,
  toggleTextOpen,
  toggleTextClosed,
  headerTrailing,
  headerAction,
  leading,
  noPadding = false,
  className = '',
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = !collapsible ? true : isControlled ? controlledIsOpen : internalOpen;
  const handleToggle =
    controlledOnToggle ?? (() => setInternalOpen((prev) => !prev));
  const toggleText = isOpen ? toggleTextOpen : toggleTextClosed;

  const handleHeaderClick = () => {
    if (collapsible) handleToggle();
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (!collapsible) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className={`uc-collapse uc-card ${styles.item} ${noPadding ? styles.noPadding : ''} ${className}`}>
      <div
        className="uc-collapse_heading"
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
        aria-controls={collapsible ? `collapse-${id}` : undefined}
      >
        {leading && <span className={styles.headerLeading}>{leading}</span>}
        <h4>{title}</h4>

        <span className={`ml-auto ${styles.headerRight}`}>
          {headerTrailing && (
            <span className={styles.headerTrailing}>{headerTrailing}</span>
          )}

          {headerAction && (
            <span
              className={styles.headerAction}
              onClick={(e) => e.stopPropagation()}
            >
              {headerAction}
            </span>
          )}

          {collapsible && (
            <span className="uc-btn btn-inline">
              {toggleText && (
                <span className="d-none d-lg-block mr-2">{toggleText}</span>
              )}

              <i className="uc-icon icon-shape--rounded">
                {isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              </i>
            </span>
          )}
        </span>
      </div>

      {isOpen && (
        <div className="uc-collapse_body" id={`collapse-${id}`}>
          <div className="uc-card_body gap-6">{children}</div>
        </div>
      )}
    </div>
  );
};

export default AccordionItem;
